/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LogInModal from 'flarum/forum/components/LogInModal';
import BookshelfDropdown from './BookshelfDropdown';
import addArticles from '../utils/addArticles';
import BookshelvesRadios from './BookshelvesRadios';
import StatusSelect from './StatusSelect';
import NoteInput from './NoteInput';
import BookshelvesListState from '../states/BookshelvesListState';
import redrawWhenSettled from '../utils/redrawWhenSettled';
import type Article from 'flarum/orell-articles/models/Article';
import type Bookshelf from '../../common/models/Bookshelf';
import type Note from '../../common/models/Note';
import type Status from '../../common/models/Status';
import type Mithril from 'mithril';

export interface ArticleBookshelfDropdownAttrs extends ComponentAttrs {
	article: Article;
	onClose?: () => void;
}

export default class ArticleBookshelfDropdown<CustomAttrs extends ArticleBookshelfDropdownAttrs = ArticleBookshelfDropdownAttrs> extends Component<
	CustomAttrs,
	BookshelvesListState | undefined
> {
	protected _loading!: boolean;
	/** `false` when the relationship is absent, `null` for the explicit "no bookshelf" option. */
	protected selectedBookshelf: Bookshelf | false | null | undefined;
	protected selectedStatus: Status | false | null | undefined;
	protected note: string | undefined;
	/** Survives `resetInputs()` so the shelf just created from inside the dropdown is still selected on reopen. */
	protected createdBookshelf: Bookshelf | null = null;

	oninit(vnode: Mithril.Vnode<CustomAttrs, this>): void {
		super.oninit(vnode);

		this.attrs.onClose = this.attrs.onClose || (() => {});

		this._loading = true;

		if (!app.session.user) {
			return;
		}

		this.state = new BookshelvesListState();
		redrawWhenSettled(this.state.refresh());

		// The list endpoint omits the bookshelf, status and note relations that `resetInputs()` reads.
		const article = this.attrs.article;
		if (article.bookshelf() === false) {
			// A failed refetch must still clear `_loading`, or the dropdown stays disabled for good.
			redrawWhenSettled(
				app.store.find<Article>(article.data.type!, article.id()!).finally(() => {
					this._loading = false;
					this.selectedStatus = app.store.all<Status>('book_status')[0];
					this.resetInputs();
				})
			);
		} else {
			this._loading = false;
			this.selectedStatus = app.store.all<Status>('book_status')[0];
			this.resetInputs();
		}
	}

	get loading(): boolean | undefined {
		return this._loading || this.state?.isInitialLoading();
	}

	view(): Mithril.Children {
		let label: Mithril.Children = '';

		if (!this.loading) {
			label = this.selectedBookshelf
				? this.selectedBookshelf.name()
				: app.translator.trans('glowingblue-orell-bookshelf.forum.article_dropdown.assign_bookshelf');
		}

		if (!app.session.user) {
			return (
				<Button className="Button Button--primary ArticleBookshelfDropdown" icon="far fa-chevron-down" onclick={() => app.modal.show(LogInModal)}>
					{label}
				</Button>
			);
		}

		return (
			<BookshelfDropdown
				className="ArticleBookshelfDropdown"
				buttonClassName="Button Button--primary"
				menuClassName="Dropdown-menu--right"
				caretIcon="far fa-chevron-down"
				label={label}
				loading={this.loading}
				onshow={this.resetInputs.bind(this)}
				onhide={this.resetInputs.bind(this)}
				disabled={this.loading}
			>
				<div className="ArticleBookshelfDropdown-content-wrapper">
					<BookshelvesRadios
						bookshelves={this.state?.getAllItems() || []}
						selected={this.selectedBookshelf}
						setBookshelf={this.setBookshelf.bind(this)}
						onCreated={this.onBookshelfCreated.bind(this)}
					/>

					<StatusSelect selected={this.selectedStatus} onSelect={this.setStatus.bind(this)} />

					<NoteInput value={this.note} onChange={this.setNote.bind(this)} />
				</div>

				<div className="ArticleBookshelfDropdown-submit_container">
					<Button className="Button Button--primary" onclick={this.saveChanges.bind(this)} disabled={!this.canSaveChanges}>
						{app.translator.trans('glowingblue-orell-bookshelf.forum.article_dropdown.save')}
					</Button>
				</div>
			</BookshelfDropdown>
		);
	}

	get canSaveChanges(): boolean {
		const { article } = this.attrs;
		const bookshelf = article.bookshelf();

		if (!this.selectedBookshelf && !bookshelf) {
			return false;
		}

		return true;
	}

	onBookshelfCreated(bookshelf: Bookshelf): void {
		this.createdBookshelf = bookshelf;
		this.selectedBookshelf = bookshelf;
		redrawWhenSettled(this.state!.refresh());
	}

	setBookshelf(bookshelf?: Bookshelf): void {
		// An explicit pick restores the ordinary reset semantics: reopening drops it again.
		this.createdBookshelf = null;
		this.selectedBookshelf = bookshelf ? bookshelf : null;
		m.redraw();
	}

	setStatus(status: Status): void {
		this.selectedStatus = status;
		m.redraw();
	}

	setNote(value: string): void {
		this.note = value;
		m.redraw();
	}

	resetInputs(): void {
		const { article } = this.attrs;
		this.selectedBookshelf = this.createdBookshelf ?? article.bookshelf();
		this.selectedStatus = article.status();
		const note = article.note();
		this.note = note ? note.text() : '';
		// A loaded note may still carry no `text` attribute.
		if (!this.note) {
			this.note = '';
		}
	}

	saveChanges(): void {
		const { article } = this.attrs;

		this._loading = true;
		m.redraw();

		let note: Note | false | undefined;
		if (this.note) {
			note = article.note();
			if (!note) {
				note = app.store.createRecord<Note>('book_notes');
			}
			note.pushAttributes({ text: this.note });
		}

		redrawWhenSettled(
			addArticles(this.selectedBookshelf || null, [article], this.selectedStatus || null, note || null)
				.then(async () => {
					await Promise.all([app.store.find<Article>(article.data.type!, article.id()!), this.state!.refresh()]);
					// The article now sits on it, so `article.bookshelf()` carries the selection from here on.
					this.createdBookshelf = null;
					this.resetInputs();

					if (this.attrs.onClose) {
						this.attrs.onClose();
					}
				})
				// A failed save must still release the dropdown.
				.finally(() => (this._loading = false))
		);
	}
}
